import { computed, onBeforeUnmount, ref, watch } from 'vue';
import {
  SFH_PRICE_CAP,
  buildPurchasePaymentInfo,
  computeItbi,
  computeNotaryFees,
  isSfhEligible,
} from 'dashboard/helper/lais/paymentInfo';
import {
  customSolicitationTypeOptions,
  isFinancingPayment,
  paymentLabel,
  paymentNeedsFgts,
} from '../utils/offerFormOptions';
import {
  formNumber,
  formatFormBRL,
  residentsSituationFromValues,
} from '../utils/offerFormatters';
import {
  itemCode,
  itemFinalidade,
  itemLocation,
  itemPublicLink,
  itemTitle,
  itemValue,
  normalizedExternalItem,
  valueFrom,
} from '../utils/offerCatalogItem';
import { offerDeadlineStatus } from '../utils/offerReadModel';

/**
 * Pricing/derived state of the "Nova proposta" wizard: purchase payment breakdown,
 * SFH gating, financing simulation, rental admin fee and the create payload.
 * Extracted verbatim from RentFlowsIndex.vue — no behaviour change.
 */
export function useOfferProposalPricing({
  proposalForm,
  selectedCatalogItem,
  selectedCatalogMode,
  catalogMode,
}) {
  let paymentDebounceTimer;
  const paymentRecalculating = ref(false);

  const isPurchaseProposalForm = computed(
    () => proposalForm.value.business_context === 'purchase'
  );

  const isRentalProposalForm = computed(
    () => proposalForm.value.business_context === 'rental'
  );

  const proposalAmountNumber = computed(() =>
    formNumber(proposalForm.value.offer_amount)
  );

  const purchasePropertyValue = computed(
    () =>
      proposalAmountNumber.value ||
      formNumber(itemValue(selectedCatalogItem.value))
  );

  const selectedCatalogImovelId = computed(
    () =>
      valueFrom(selectedCatalogItem.value, 'id', 'imovel_id', 'imovelId') || ''
  );

  const selectedCatalogCity = computed(
    () => valueFrom(selectedCatalogItem.value, 'cidade', 'city') || ''
  );

  const selectedCatalogUf = computed(
    () => valueFrom(selectedCatalogItem.value, 'uf', 'state') || ''
  );

  const isFinancingProposalPayment = computed(() =>
    isFinancingPayment(proposalForm.value.payment_kind)
  );

  const isSfhEligibleForProposal = computed(() =>
    isSfhEligible(purchasePropertyValue.value)
  );

  const showSfhOption = computed(
    () => isPurchaseProposalForm.value && isFinancingProposalPayment.value
  );

  const sfhLimitLabel = computed(() => formatFormBRL(SFH_PRICE_CAP));

  const paymentBreakdownTotal = computed(
    () =>
      formNumber(
        proposalForm.value.entry_amount || proposalForm.value.cash_amount
      ) +
      (paymentNeedsFgts(proposalForm.value.payment_kind)
        ? formNumber(proposalForm.value.fgts_amount)
        : 0) +
      (isFinancingPayment(proposalForm.value.payment_kind)
        ? formNumber(proposalForm.value.financed_amount)
        : 0)
  );

  const paymentBreakdownDifference = computed(
    () => proposalAmountNumber.value - paymentBreakdownTotal.value
  );

  const paymentBreakdownHint = computed(() => {
    if (!isPurchaseProposalForm.value || !proposalAmountNumber.value) return '';

    const sumText = `Soma dos valores: ${formatFormBRL(paymentBreakdownTotal.value)}`;
    if (Math.abs(paymentBreakdownDifference.value) < 1) return sumText;

    return `Os valores precisam somar ${formatFormBRL(proposalAmountNumber.value)}. ${sumText}`;
  });

  const purchasePaymentInfo = computed(() =>
    buildPurchasePaymentInfo({
      propertyValue: purchasePropertyValue.value,
      paymentKind: proposalForm.value.payment_kind,
      uf: selectedCatalogUf.value,
      city: selectedCatalogCity.value,
      firstPurchase: proposalForm.value.first_purchase,
      hasSfh: proposalForm.value.has_sfh,
      itbiValue: proposalForm.value.itbi_amount,
      itbiOverridden: proposalForm.value.itbi_overridden,
      notaryFeeValue: proposalForm.value.notary_fee_value,
      notaryFeeOverridden: proposalForm.value.notary_fee_overridden,
      brokerageFeeValue: proposalForm.value.brokerage_fee_value,
      financedAmount: proposalForm.value.financed_amount,
      termMonths: proposalForm.value.financing_term_months,
      annualRate: proposalForm.value.financing_annual_rate,
    })
  );

  const purchasePaymentRows = computed(() => purchasePaymentInfo.value.rows);

  const financingSimulation = computed(
    () => purchasePaymentInfo.value.financing
  );

  const financingInstallmentLabel = computed(() =>
    financingSimulation.value?.monthlyInstallment
      ? formatFormBRL(financingSimulation.value.monthlyInstallment)
      : ''
  );

  const syncPaymentFeeDefaults = () => {
    if (!isPurchaseProposalForm.value || !purchasePropertyValue.value) return;

    if (!proposalForm.value.itbi_overridden) {
      proposalForm.value.itbi_amount = String(
        computeItbi({
          propertyValue: purchasePropertyValue.value,
          uf: selectedCatalogUf.value,
          city: selectedCatalogCity.value,
          firstPurchase: proposalForm.value.first_purchase,
          hasSfh: proposalForm.value.has_sfh && isSfhEligibleForProposal.value,
        })
      );
    }
    if (!proposalForm.value.notary_fee_overridden) {
      proposalForm.value.notary_fee_value = String(
        computeNotaryFees({
          propertyValue: purchasePropertyValue.value,
        })
      );
    }
  };

  function syncPaymentBreakdownDefaultsNow() {
    clearTimeout(paymentDebounceTimer);
    paymentRecalculating.value = false;
    if (!isPurchaseProposalForm.value) return;

    const amount = proposalAmountNumber.value;
    if (!amount) return;

    if (!isFinancingPayment(proposalForm.value.payment_kind)) {
      proposalForm.value.has_sfh = false;
    }
    if (!isSfhEligibleForProposal.value) {
      proposalForm.value.has_sfh = false;
    }
    if (!paymentNeedsFgts(proposalForm.value.payment_kind)) {
      proposalForm.value.fgts_amount = '';
    }
    if (!isFinancingPayment(proposalForm.value.payment_kind)) {
      proposalForm.value.financed_amount = '';
    }
    if (!proposalForm.value.brokerage_fee_value) {
      proposalForm.value.brokerage_fee_value = String(
        Math.round(amount * 0.06)
      );
    }
    if (!proposalForm.value.entry_amount && !proposalForm.value.cash_amount) {
      const remaining =
        amount -
        formNumber(proposalForm.value.fgts_amount) -
        formNumber(proposalForm.value.financed_amount);
      proposalForm.value.entry_amount = String(Math.max(remaining, 0));
      proposalForm.value.cash_amount = proposalForm.value.entry_amount;
    } else if (proposalForm.value.entry_amount) {
      proposalForm.value.cash_amount = proposalForm.value.entry_amount;
    }
    syncPaymentFeeDefaults();
  }

  const schedulePaymentBreakdownDefaults = () => {
    clearTimeout(paymentDebounceTimer);
    paymentRecalculating.value = false;
    if (!isPurchaseProposalForm.value) return;

    paymentRecalculating.value = true;
    paymentDebounceTimer = setTimeout(() => {
      syncPaymentBreakdownDefaultsNow();
      paymentRecalculating.value = false;
    }, 300);
  };

  const recalculatePaymentFees = () => {
    proposalForm.value.itbi_overridden = false;
    proposalForm.value.notary_fee_overridden = false;
    syncPaymentBreakdownDefaultsNow();
  };

  watch(selectedCatalogItem, () => {
    if (isPurchaseProposalForm.value) syncPaymentBreakdownDefaultsNow();
  });

  onBeforeUnmount(() => {
    clearTimeout(paymentDebounceTimer);
  });

  const paymentBreakdownMetadata = () => ({
    payment_breakdown: {
      final_sale_amount: proposalForm.value.offer_amount || null,
      brokerage_fee_value: proposalForm.value.brokerage_fee_value || null,
      brokerage_fee_percentage: isPurchaseProposalForm.value ? 6 : null,
      earnest_payment_amount: proposalForm.value.earnest_payment_amount || null,
      entry_amount:
        proposalForm.value.entry_amount ||
        proposalForm.value.cash_amount ||
        null,
      cash_amount:
        proposalForm.value.entry_amount ||
        proposalForm.value.cash_amount ||
        null,
      fgts_amount: paymentNeedsFgts(proposalForm.value.payment_kind)
        ? proposalForm.value.fgts_amount || null
        : null,
      financed_amount: isFinancingPayment(proposalForm.value.payment_kind)
        ? proposalForm.value.financed_amount || null
        : null,
      itbi_amount: proposalForm.value.itbi_amount || null,
      itbi_overridden: proposalForm.value.itbi_overridden,
      notary_fee_value: proposalForm.value.notary_fee_value || null,
      notary_fee_overridden: proposalForm.value.notary_fee_overridden,
      acquisition_total_amount: purchasePaymentInfo.value.total || null,
      estimated_monthly_installment:
        financingSimulation.value?.monthlyInstallment || null,
      financing_term_months: proposalForm.value.financing_term_months || null,
      financing_annual_rate: proposalForm.value.financing_annual_rate || null,
      sfh_price_cap: SFH_PRICE_CAP,
      first_purchase: proposalForm.value.first_purchase,
      has_sfh: proposalForm.value.has_sfh,
      payment_method_label: paymentLabel(proposalForm.value.payment_kind),
    },
  });

  const rentalAdminFeePercentage = computed(() => {
    const percentage = formNumber(proposalForm.value.admin_fee_percentage);
    return percentage > 0 ? percentage : 0;
  });

  const rentalAdminFeeValue = computed(() => {
    if (!isRentalProposalForm.value) return 0;

    const percentageFee =
      (proposalAmountNumber.value * rentalAdminFeePercentage.value) / 100;
    return Math.max(
      percentageFee,
      formNumber(proposalForm.value.minimum_admin_fee)
    );
  });

  const rentalOwnerReceiveValue = computed(() => {
    if (!isRentalProposalForm.value || !proposalAmountNumber.value) return 0;

    return Math.max(proposalAmountNumber.value - rentalAdminFeeValue.value, 0);
  });

  const rentalResidentsSituation = computed(() =>
    residentsSituationFromValues(
      proposalForm.value.residents_count,
      proposalForm.value.has_pets
    )
  );

  const rentalCustomSolicitations = () => {
    if (!proposalForm.value.custom_solicitation_type) return [];

    return [
      {
        type: proposalForm.value.custom_solicitation_type,
        description:
          proposalForm.value.custom_solicitation_description ||
          customSolicitationTypeOptions.find(
            option =>
              option.value === proposalForm.value.custom_solicitation_type
          )?.label ||
          'Ajuste solicitado no imóvel',
      },
    ];
  };

  const rentalBreakdownMetadata = () => ({
    rental_breakdown: {
      rent_amount: proposalForm.value.offer_amount || null,
      iptu_amount: proposalForm.value.rent_iptu_amount || null,
      condominium_amount: proposalForm.value.rent_condominium_amount || null,
      admin_fee_percentage: rentalAdminFeePercentage.value,
      admin_fee_value: rentalAdminFeeValue.value,
      minimum_fee_value: proposalForm.value.minimum_admin_fee || null,
      owner_receive_amount: rentalOwnerReceiveValue.value,
      residents_count: proposalForm.value.residents_count || null,
      has_pets: proposalForm.value.has_pets,
      residents_situation: rentalResidentsSituation.value || null,
      custom_solicitations: rentalCustomSolicitations(),
    },
  });

  const deadlineMetadata = () =>
    proposalForm.value.response_deadline_date
      ? {
          response_deadline_date: proposalForm.value.response_deadline_date,
          response_deadline_source: 'dashboard_rent_offer_flow',
        }
      : {};

  const selectedCatalogSummary = computed(() => {
    if (!selectedCatalogItem.value) return null;
    return {
      title: itemTitle(selectedCatalogItem.value),
      code: itemCode(selectedCatalogItem.value),
      location:
        itemLocation(selectedCatalogItem.value) || 'Localização não informada',
      value: itemValue(selectedCatalogItem.value),
      link: itemPublicLink(selectedCatalogItem.value),
    };
  });

  const proposalDeadlinePreview = computed(() =>
    proposalForm.value.response_deadline_date
      ? offerDeadlineStatus({
          status: 'submitted',
          metadata: {
            response_deadline_date: proposalForm.value.response_deadline_date,
          },
        })
      : ''
  );

  // Applies the catalog item to the proposal form (value suggestion + rental defaults).
  const applyCatalogItemToProposal = item => {
    const suggestedValue = itemValue(item);
    if (!proposalForm.value.offer_amount && suggestedValue) {
      proposalForm.value.offer_amount = suggestedValue;
    }
    if (itemFinalidade(item) === 'aluguel' || itemFinalidade(item) === 'rent') {
      proposalForm.value.business_context = 'rental';
      proposalForm.value.rent_iptu_amount =
        valueFrom(item, 'valor_iptu', 'valorIptu', 'property_tax') || '';
      proposalForm.value.rent_condominium_amount =
        valueFrom(item, 'valor_condominio', 'valorCondominio', 'condo_price') ||
        '';
    } else {
      proposalForm.value.business_context = 'purchase';
    }
  };

  const createPayload = () => {
    syncPaymentBreakdownDefaultsNow();
    const {
      requester_name,
      requester_email,
      requester_phone,
      ...offerPayload
    } = proposalForm.value;
    delete offerPayload.earnest_payment_amount;
    delete offerPayload.entry_amount;
    delete offerPayload.cash_amount;
    delete offerPayload.fgts_amount;
    delete offerPayload.brokerage_fee_value;
    delete offerPayload.itbi_amount;
    delete offerPayload.itbi_overridden;
    delete offerPayload.notary_fee_value;
    delete offerPayload.notary_fee_overridden;
    delete offerPayload.financing_term_months;
    delete offerPayload.financing_annual_rate;
    delete offerPayload.rent_iptu_amount;
    delete offerPayload.rent_condominium_amount;
    delete offerPayload.admin_fee_percentage;
    delete offerPayload.minimum_admin_fee;
    delete offerPayload.residents_count;
    delete offerPayload.has_pets;
    delete offerPayload.custom_solicitation_type;
    delete offerPayload.custom_solicitation_description;
    delete offerPayload.response_deadline_date;
    if (proposalForm.value.business_context === 'rental') {
      delete offerPayload.payment_kind;
      delete offerPayload.financed_amount;
      delete offerPayload.first_purchase;
      delete offerPayload.has_sfh;
    }
    const selectedItemMode = selectedCatalogMode.value || catalogMode.value;
    const propertyPayload =
      selectedItemMode === 'portfolio'
        ? { imovel_id: valueFrom(selectedCatalogItem.value, 'id') }
        : {
            external_imovel: normalizedExternalItem(
              selectedCatalogItem.value,
              selectedItemMode
            ),
          };
    const metadata = {
      ...(offerPayload.metadata || {}),
      ...deadlineMetadata(),
      ...(isPurchaseProposalForm.value
        ? paymentBreakdownMetadata()
        : rentalBreakdownMetadata()),
    };

    return {
      ...offerPayload,
      ...propertyPayload,
      metadata,
      requester: {
        name: requester_name.trim(),
        email: requester_email.trim(),
        phone_number: requester_phone.trim(),
      },
    };
  };

  return {
    paymentRecalculating,
    isPurchaseProposalForm,
    isRentalProposalForm,
    proposalAmountNumber,
    purchasePropertyValue,
    selectedCatalogImovelId,
    selectedCatalogCity,
    selectedCatalogUf,
    isFinancingProposalPayment,
    isSfhEligibleForProposal,
    showSfhOption,
    sfhLimitLabel,
    paymentBreakdownTotal,
    paymentBreakdownDifference,
    paymentBreakdownHint,
    purchasePaymentInfo,
    purchasePaymentRows,
    financingSimulation,
    financingInstallmentLabel,
    syncPaymentBreakdownDefaultsNow,
    schedulePaymentBreakdownDefaults,
    recalculatePaymentFees,
    rentalAdminFeePercentage,
    rentalAdminFeeValue,
    rentalOwnerReceiveValue,
    rentalResidentsSituation,
    selectedCatalogSummary,
    proposalDeadlinePreview,
    applyCatalogItemToProposal,
    createPayload,
  };
}
